import { TWO_FACTOR_TIMEOUT_SECONDS } from '@/app/config/app.config';
import { useAuth } from '@/app/context';
import { AuthHeader, TwoFactorForm } from '@/features/auth/components';
import { getPostAuthRedirectPath } from '@/features/auth/utils/get-post-auth-redirect-path.util';
import { LoadingSpinner } from '@/shared/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_auth/two-factor')({
  component: TwoFactorPage,
  pendingComponent: LoadingSpinner,
});

function TwoFactorPage() {
  const [error, setError] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const {
    signInWithMfaCode,
    resendMfaCode,
    loading,
    isAuthenticated,
    attributes,
    getCurrentUserValues,
  } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && attributes) {
      navigate({ to: getPostAuthRedirectPath(attributes) });
    }
  }, [isAuthenticated, navigate, attributes]);

  const handleSubmit = async (code: string) => {
    setError('');
    const result = await signInWithMfaCode(code);
    const userCurrentValues = await getCurrentUserValues();
    if (result.isSignedIn) {
      setAttemptCount(0);
      navigate({ to: getPostAuthRedirectPath(userCurrentValues?.attributes) });
    } else {
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      let errorMessage = result.message;

      if (
        errorMessage.includes('Invalid session') ||
        errorMessage.includes('session is expired')
      ) {
        errorMessage = 'Esse código está expirado.';
      } else if (
        errorMessage.includes('Invalid code') ||
        errorMessage.includes('auth state')
      ) {
        errorMessage = 'Código incorreto ou expirado';
      }

      if (newAttemptCount >= 3) {
        errorMessage = 'Muitas tentativas inválidas. Faça login novamente.';
        setTimeout(() => {
          navigate({ to: '/login' });
        }, 10000);
      }

      setError(errorMessage);
    }
  };

  const handleResend = async () => {
    setError('');
    // `resendMfaCode` devolve void: hoje ele apenas redireciona para o login
    // (ver auth.provider). O antigo `if (result.status !== 200)` conferia um
    // campo que a função nunca produziu — branch morto, não tratamento de erro.
    await resendMfaCode();
  };

  return (
    <>
      <AuthHeader
        title='Confirme sua identidade.'
        subtitle='Digite o código enviado para o seu e-mail.'
      />

      <TwoFactorForm
        onSubmit={handleSubmit}
        onResend={handleResend}
        loading={loading}
        error={error}
        totalSeconds={TWO_FACTOR_TIMEOUT_SECONDS}
      />
    </>
  );
}
